import React, { useState, useEffect, useCallback } from 'react';
import { X, Syringe, Stethoscope, Scissors, Package, Banknote, CreditCard, Clock, Filter, User, PawPrint, Wallet, CalendarClock, DoorOpen } from 'lucide-react';
import { getOperations, getPaymentDetails, getClinicStaff, getBoardingRevenue } from '../services/api';
import PetAvatar from './PetAvatar';

const MODE_CONFIG = {
  vaccine: { label: 'Aşı Geçmişi', icon: Syringe, color: 'text-[#34C759]', bg: 'bg-green-50', badge: 'bg-green-100 text-green-700', dataMode: 'operation' },
  treatment: { label: 'Tedavi Geçmişi', icon: Stethoscope, color: 'text-[#6b6de3]', bg: 'bg-blue-50', badge: 'bg-blue-100 text-blue-700', dataMode: 'operation' },
  service: { label: 'Hizmet Geçmişi', icon: Scissors, color: 'text-[#FF9500]', bg: 'bg-orange-50', badge: 'bg-orange-100 text-orange-700', dataMode: 'operation' },
  product: { label: 'Ürün Geçmişi', icon: Package, color: 'text-[#AF52DE]', bg: 'bg-purple-50', badge: 'bg-purple-100 text-purple-700', dataMode: 'operation' },
  cash: { label: 'Nakit Detayı', icon: Banknote, color: 'text-emerald-600', bg: 'bg-emerald-50', badge: 'bg-emerald-100 text-emerald-700', dataMode: 'payment' },
  card: { label: 'Kredi Kartı Detayı', icon: CreditCard, color: 'text-sky-600', bg: 'bg-sky-50', badge: 'bg-sky-100 text-sky-700', dataMode: 'payment' },
  credit: { label: 'Veresiye Detayı', icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50', badge: 'bg-amber-100 text-amber-700', dataMode: 'payment' },
  boarding: { label: 'Konaklama Geçmişi', icon: DoorOpen, color: 'text-[#FF9500]', bg: 'bg-amber-50', badge: 'bg-amber-100 text-amber-700', dataMode: 'boarding' },
};

const TYPE_BADGES = {
  vaccine: { label: 'Aşı', cls: 'bg-green-100 text-green-700' },
  treatment: { label: 'Tedavi', cls: 'bg-blue-100 text-blue-700' },
  service: { label: 'Hizmet', cls: 'bg-orange-100 text-orange-700' },
  product: { label: 'Ürün', cls: 'bg-purple-100 text-purple-700' },
};

const PAYMENT_LABELS = { cash: 'Nakit', card: 'Kart', split: 'Parçalı', credit: 'Veresiye' };

const getPaymentLabel = (item, mode) => {
  const pm = item.payment_method;
  const isPaymentMode = ['cash', 'card', 'credit'].includes(mode);
  const fc = formatCurrency;
  const creditRemaining = item.is_credit ? (item.price || 0) - (item.credit_collected || 0) : 0;

  // Non-credit split
  if (pm === 'split' && !item.is_credit) {
    if (mode === 'cash') return `Parçalı (${fc(item.card_amount || 0)} KK)`;
    if (mode === 'card') return `Parçalı (${fc(item.cash_amount || 0)} Nakit)`;
    return `Parçalı (${fc(item.cash_amount || 0)} Nakit, ${fc(item.card_amount || 0)} KK)`;
  }

  // Credit items
  if (item.is_credit) {
    const collected = item.credit_collected || 0;
    const cm = item.credit_collected_method;
    const isSplitCollect = cm === 'split';

    if (mode === 'cash') {
      // Cash modal: show other portions (card + credit remaining)
      const others = [];
      if (isSplitCollect && item.card_amount > 0) others.push(`${fc(item.card_amount)} KK`);
      if (creditRemaining > 0) others.push(`${fc(creditRemaining)} Veresiye`);
      const prefix = isSplitCollect ? 'Parçalı + Veresiye' : '';
      return others.length ? `${prefix} (${others.join(' + ')})`.trim() : 'Nakit';
    }

    if (mode === 'card') {
      // Card modal: show other portions (cash + credit remaining)
      const others = [];
      if (isSplitCollect && item.cash_amount > 0) others.push(`${fc(item.cash_amount)} Nakit`);
      if (creditRemaining > 0) others.push(`${fc(creditRemaining)} Veresiye`);
      const prefix = isSplitCollect ? 'Parçalı + Veresiye' : '';
      return others.length ? `${prefix} (${others.join(' + ')})`.trim() : 'Kart';
    }

    if (mode === 'credit') {
      // Credit modal: show what was collected
      if (collected > 0) {
        if (isSplitCollect) {
          const parts = [];
          if (item.cash_amount > 0) parts.push(`${fc(item.cash_amount)} Nakit`);
          if (item.card_amount > 0) parts.push(`${fc(item.card_amount)} KK`);
          return parts.length ? `Parçalı (${parts.join(' + ')})` : 'Veresiye';
        }
        const methodLabel = cm === 'card' ? 'KK' : 'Nakit';
        return `(${fc(collected)} ${methodLabel})`;
      }
      return 'Veresiye';
    }

    // Operation history modals: full breakdown
    if (collected > 0) {
      if (isSplitCollect) {
        const parts = [];
        if (item.cash_amount > 0) parts.push(`${fc(item.cash_amount)} Nakit`);
        if (item.card_amount > 0) parts.push(`${fc(item.card_amount)} KK`);
        return `Parçalı + Veresiye (${parts.join(', ')}, ${fc(creditRemaining)} kalan)`;
      }
      const methodLabel = cm === 'card' ? 'KK' : 'Nakit';
      return `Veresiye (${fc(collected)} ${methodLabel}, ${fc(creditRemaining)} kalan)`;
    }
    return 'Veresiye';
  }

  return PAYMENT_LABELS[pm] || pm || 'Nakit';
};

const genderEmoji = (g) => {
  if (!g) return '';
  const gl = g.toLowerCase();
  if (gl === 'erkek' || gl === 'male' || gl === 'e') return ' ♂️';
  if (gl === 'dişi' || gl === 'disi' || gl === 'female' || gl === 'd') return ' ♀️';
  return '';
};

const formatCurrency = (amount) =>
  new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 0 }).format(amount);

const formatDateTime = (dateStr) => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = String(d.getFullYear()).slice(-2);
    const hour = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hour}:${min}`;
  } catch { return dateStr; }
};

const formatDateRange = (date, dateFrom, dateTo) => {
  if (dateFrom && dateTo) {
    try {
      const f = new Date(dateFrom + 'T12:00:00').toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
      const t = new Date(dateTo + 'T12:00:00').toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
      return `${f} - ${t}`;
    } catch { return `${dateFrom} ~ ${dateTo}`; }
  }
  if (date) {
    try {
      return new Date(date + 'T12:00:00').toLocaleDateString('tr-TR', { day: 'numeric', month: 'long' });
    } catch { return date; }
  }
  return 'Bugün';
};

const UnifiedHistoryModal = ({ open, onClose, mode, date, dateFrom, dateTo }) => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [staff, setStaff] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState('');

  const config = MODE_CONFIG[mode];
  const isPaymentMode = config?.dataMode === 'payment';
  const isBoardingMode = config?.dataMode === 'boarding';

  const loadData = useCallback(async () => {
    if (!open || !mode || !config) return;
    setLoading(true);
    try {
      const [staffRes] = await Promise.all([getClinicStaff()]);
      setStaff(staffRes.data || []);

      if (isBoardingMode) {
        const res = await getBoardingRevenue(
          dateFrom || date || undefined,
          dateTo || date || undefined,
          selectedStaff || undefined
        );
        const records = res.data?.records || [];
        const mapped = records.map(r => ({
          id: r.id,
          name: r.pet_name || 'Bilinmiyor',
          type: 'boarding',
          amount: r.total_cost || 0,
          price: r.total_cost || 0,
          customer_name: r.customer_name || '',
          pet_name: r.pet_name || '',
          pet_gender: r.pet_gender || '',
          pet_species: r.pet_species || '',
          staff_name: r.user_name || '',
          payment_method: r.is_free ? 'free' : 'cash',
          is_credit: false,
          cash_amount: 0,
          card_amount: 0,
          credit_collected: 0,
          credit_collected_method: '',
          date: r.check_out || r.check_in || '',
          quantity: 1,
          check_in: r.check_in,
          check_out: r.check_out,
          is_free: r.is_free,
          room_name: r.room_name,
          cage_name: r.cage_name,
        }));
        setItems(mapped);
        setTotal(res.data?.total || 0);
      } else if (isPaymentMode) {
        const res = await getPaymentDetails(mode, date, selectedStaff || undefined, dateFrom, dateTo);
        const payItems = (res.data.items || []).map(item => ({
          id: item.id,
          name: item.name,
          type: item.type,
          amount: item.amount,
          price: item.price,
          customer_name: item.customer_name,
          pet_name: item.pet_name,
          pet_gender: item.pet_gender,
          pet_species: item.pet_species || '',
          staff_name: item.vet_name,
          payment_method: item.payment_method,
          is_credit: item.is_credit,
          cash_amount: item.cash_amount || 0,
          card_amount: item.card_amount || 0,
          credit_collected: item.credit_collected || 0,
          credit_collected_method: item.credit_collected_method || '',
          date: item.date,
          quantity: 1,
        }));
        setItems(payItems);
        setTotal(res.data.total || 0);
      } else {
        const params = { op_type: mode, limit: 500 };
        if (dateFrom && dateTo) {
          params.date_from = dateFrom;
          params.date_to = dateTo;
        } else if (date) {
          params.date_from = date;
          params.date_to = date;
        }
        if (selectedStaff) params.user_id = selectedStaff;
        const res = await getOperations(params);
        const ops = res.data || [];
        const mapped = ops.map(op => ({
          id: op.id,
          name: op.name,
          type: op.type,
          amount: op.price,
          price: op.price,
          customer_name: op.customer_name,
          pet_name: op.pet_name,
          pet_gender: op.pet_gender,
          pet_species: op.pet_species || '',
          staff_name: op.user_name,
          payment_method: op.payment_method,
          is_credit: op.is_credit,
          cash_amount: op.cash_amount || 0,
          card_amount: op.card_amount || 0,
          credit_collected: op.credit_collected || 0,
          credit_collected_method: op.credit_collected_method || '',
          date: op.date,
          quantity: op.quantity,
        }));
        setItems(mapped);
        setTotal(ops.reduce((sum, op) => sum + (op.price || 0), 0));
      }
    } catch {
      setItems([]);
      setTotal(0);
    }
    setLoading(false);
  }, [open, mode, date, dateFrom, dateTo, selectedStaff, config, isPaymentMode, isBoardingMode]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (!open) { setSelectedStaff(''); }
  }, [open]);

  if (!open || !config) return null;

  const Icon = config.icon;

  return (
    <div className="fixed inset-x-0 top-0 bottom-[65px] z-[9998] flex sm:inset-0 sm:items-center sm:justify-center sm:p-3" data-testid="unified-history-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full sm:max-w-md lg:max-w-3xl bg-white sm:rounded-2xl flex flex-col h-full sm:h-auto sm:max-h-[75vh] shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-4 lg:px-6 py-3 lg:py-4 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-2.5 lg:gap-3">
            <div className={`w-10 h-10 lg:w-12 lg:h-12 ${config.bg} rounded-xl flex items-center justify-center`}>
              <Icon size={20} className={`${config.color} lg:hidden`} />
              <Icon size={24} className={`${config.color} hidden lg:block`} />
            </div>
            <div>
              <h3 className="text-sm lg:text-xl font-bold text-gray-900" data-testid="modal-title">{config.label}</h3>
              <p className="text-xs lg:text-sm text-[#8E8E93]">
                {formatDateRange(date, dateFrom, dateTo)} · {items.length} kayıt
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors" data-testid="modal-close-btn">
            <X size={22} className="text-gray-400 sm:hidden" />
            <X size={20} className="text-gray-400 hidden sm:block" />
          </button>
        </div>

        {/* Staff Filter + Total */}
        <div className="px-4 lg:px-6 py-2.5 lg:py-3 border-b border-gray-100 flex items-center gap-2 lg:gap-3 shrink-0">
          <div className="flex items-center gap-1.5 lg:gap-2 flex-1 min-w-0">
            <Filter size={15} className="text-[#8E8E93] flex-shrink-0 lg:hidden" />
            <Filter size={18} className="text-[#8E8E93] flex-shrink-0 hidden lg:block" />
            <select
              value={selectedStaff}
              onChange={(e) => setSelectedStaff(e.target.value)}
              className="flex-1 text-sm lg:text-base bg-[#F2F2F7] border-0 rounded-lg px-3 py-2 lg:py-2.5 focus:outline-none focus:ring-2 focus:ring-[#6b6de3]/30 min-w-0"
              data-testid="modal-staff-filter"
            >
              <option value="">Tüm Ekip</option>
              {staff.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
          <div className={`${config.badge} px-3 lg:px-5 py-1.5 lg:py-2 rounded-lg text-center flex-shrink-0`}>
            <p className="text-sm lg:text-lg font-bold" data-testid="modal-total">{formatCurrency(total)}</p>
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto p-3 lg:p-4 space-y-2 lg:space-y-2.5">
          {loading ? (
            <div className="flex justify-center py-10">
              <div className="w-7 h-7 border-2 border-[#6b6de3] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-10 text-[#8E8E93]">
              <Icon size={36} className="mx-auto mb-3 opacity-30" />
              <p className="text-sm lg:text-base">Bu filtreyle eşleşen kayıt yok</p>
            </div>
          ) : (
            items.map((item, i) => {
              const typeBadge = TYPE_BADGES[item.type] || { label: item.type, cls: 'bg-gray-100 text-gray-700' };
              const isBoardingItem = item.type === 'boarding';
              return (
                <div key={item.id || i} className="bg-[#F8F8FA] rounded-xl px-3 lg:px-5 py-2.5 lg:py-3 flex gap-2.5 lg:gap-3" data-testid={`history-item-${i}`}>
                  {/* Pet Icon + Name - spans 2 rows */}
                  <div className="flex flex-col items-center justify-center flex-shrink-0 w-12 lg:w-14">
                    <PetAvatar species={item.pet_species} gender={item.pet_gender} size={18} className="lg:hidden" />
                    <PetAvatar species={item.pet_species} gender={item.pet_gender} size={22} className="hidden lg:flex" />
                    {item.pet_name && (
                      <span className="text-[10px] lg:text-xs font-medium text-gray-600 mt-0.5 truncate w-full text-center leading-tight">{item.pet_name}</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    {/* Row 1: Name + Type + DateTime + Amount */}
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0 flex-1 flex items-center gap-1.5 lg:gap-2 flex-wrap">
                        <span className="text-sm lg:text-base font-semibold text-gray-900">{item.name}</span>
                        {!isBoardingItem && <span className={`text-[10px] lg:text-xs px-1.5 lg:px-2 py-0.5 rounded-full font-medium ${typeBadge.cls}`}>{typeBadge.label}</span>}
                        {isBoardingItem && item.room_name && (
                          <span className="text-[10px] lg:text-xs px-1.5 lg:px-2 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700">{item.room_name}{item.cage_name ? ` / ${item.cage_name}` : ''}</span>
                        )}
                        {item.quantity > 1 && (
                          <span className="text-[10px] lg:text-xs px-1.5 lg:px-2 py-0.5 rounded-full font-medium bg-orange-100 text-orange-700">x{item.quantity}</span>
                        )}
                        {item.date && (
                          <span className="flex items-center gap-0.5 text-[10px] lg:text-xs text-[#8E8E93]"><CalendarClock size={10} className="lg:hidden" /><CalendarClock size={12} className="hidden lg:inline" />{formatDateTime(item.date)}</span>
                        )}
                      </div>
                      <span className={`text-sm lg:text-base font-bold flex-shrink-0 ${isBoardingItem && item.is_free ? 'text-[#34C759]' : 'text-gray-900'}`}>{isBoardingItem && item.is_free ? 'Ücretsiz' : formatCurrency(item.amount)}</span>
                    </div>
                    {/* Row 2: Customer + Staff/Room + Payment/Dates */}
                    <div className="flex items-center gap-x-3 lg:gap-x-4 gap-y-0.5 flex-wrap mt-1 lg:mt-1.5 text-xs lg:text-sm text-[#8E8E93]">
                      {item.customer_name && (
                        <span className="flex items-center gap-0.5 truncate max-w-[120px] lg:max-w-[200px]"><User size={11} className="lg:hidden" /><User size={14} className="hidden lg:inline" />{item.customer_name}</span>
                      )}
                      {!isBoardingItem && item.staff_name && (
                        <span className="flex items-center gap-0.5"><Stethoscope size={11} className="lg:hidden" /><Stethoscope size={14} className="hidden lg:inline" />{item.staff_name}</span>
                      )}
                      {isBoardingItem && item.check_in && (
                        <span className="flex items-center gap-0.5"><CalendarClock size={11} className="lg:hidden" /><CalendarClock size={14} className="hidden lg:inline" />{formatDateTime(item.check_in)} → {item.check_out ? formatDateTime(item.check_out) : '...'}</span>
                      )}
                      {!isBoardingItem && item.payment_method && (
                        <span className="flex items-center gap-0.5"><Wallet size={11} className="lg:hidden" /><Wallet size={14} className="hidden lg:inline" />{getPaymentLabel(item, mode)}</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default UnifiedHistoryModal;
